import { Check, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { PERMISSION_LABELS, getModuleLabelKey, getPermissionLabel } from '@/lib/permission-labels';

/** Subset of a subscription plan needed to derive "what's included". */
export interface IncludedPlan {
  maxUsers: number | null;
  maxProducts: number | null;
  maxLocations: number | null;
  maxPromotions?: number | null;
  maxTables?: number | null;
  includedModules?: string[] | null;
  permissionKeys?: string[] | null;
}

/** Subset of a subscription add-on needed to derive "what's included". */
export interface IncludedAddon {
  type: string; // 'MODULE' | 'QUANTITY'
  permissionKeys?: string[] | null;
  limitType?: string | null;
  limitPerUnit?: number | null;
  unit?: string | null;
}

type IncludedFeaturesProps =
  | { kind: 'plan'; plan: IncludedPlan; max?: number; className?: string }
  | { kind: 'addon'; addon: IncludedAddon; max?: number; className?: string };

interface FeatureRow {
  id: string;
  text: string;
  /** false = shown muted with a dash (e.g. "Not included"). */
  included: boolean;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

const QTY_KEYS = new Set(['max_users', 'max_products', 'max_locations', 'max_promotions', 'max_tables']);

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function limitRow(id: string, labelKey: string, value: number | null | undefined, t: Translate): FeatureRow | null {
  if (value === undefined) return null; // not provided by this surface
  const label = t(labelKey);
  // Same semantics as the tenant UsageBar: null = unlimited, 0 = not included.
  if (value === null) return { id, text: t('includesLimitValue', { label, value: t('includesUnlimited') }), included: true };
  if (value === 0) return { id, text: t('includesLimitValue', { label, value: t('includesNotIncluded') }), included: false };
  return { id, text: t('includesLimitValue', { label, value: value.toLocaleString() }), included: true };
}

function planRows(plan: IncludedPlan, t: Translate): FeatureRow[] {
  const rows = [
    limitRow('users', 'includesUsers', plan.maxUsers, t),
    limitRow('products', 'includesProducts', plan.maxProducts, t),
    limitRow('locations', 'includesLocations', plan.maxLocations, t),
    limitRow('promotions', 'includesPromotions', plan.maxPromotions, t),
    limitRow('tables', 'includesTables', plan.maxTables, t),
  ].filter((r): r is FeatureRow => r !== null);

  // Modules: prefer the plan's explicit includedModules; fall back to the modules
  // implied by its permission keys.
  let moduleKeys: string[] = Array.isArray(plan.includedModules) ? plan.includedModules : [];
  if (moduleKeys.length === 0 && Array.isArray(plan.permissionKeys)) {
    moduleKeys = plan.permissionKeys.filter((k) => k !== '*').map((k) => k.split(':')[0]);
  }
  const seen = new Set<string>();
  for (const key of moduleKeys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const labelKey = getModuleLabelKey(key);
    rows.push({ id: `mod:${key}`, text: labelKey ? t(labelKey) : capitalize(key), included: true });
  }
  return rows;
}

/** Unit strings come from the catalog ("pack/month"); translate each word. */
function translateUnit(t: Translate, unit: string): string {
  return unit
    .split('/')
    .map((w) => {
      const word = w.trim();
      return t(`unitWord_${word}`, { defaultValue: word });
    })
    .join('/');
}

function addonRows(addon: IncludedAddon, t: Translate): FeatureRow[] {
  if (addon.type === 'QUANTITY') {
    if (!addon.limitType) return [];
    const n = addon.limitPerUnit ?? 1;
    const text = QTY_KEYS.has(addon.limitType)
      ? t(`includesQty_${addon.limitType}`, { n })
      : t('includesQtyGeneric', { n, unit: translateUnit(t, addon.unit ?? addon.limitType) });
    return [{ id: 'qty', text, included: true }];
  }

  // MODULE: one row per module (deduped), preferring the module's wildcard label.
  const byModule = new Map<string, { key: string; wildcard: boolean }>();
  for (const key of addon.permissionKeys ?? []) {
    if (key === '*') continue;
    const module = PERMISSION_LABELS[key]?.module ?? key.split(':')[0];
    const wildcard = key.endsWith(':*');
    const current = byModule.get(module);
    if (!current || (wildcard && !current.wildcard)) byModule.set(module, { key, wildcard });
  }
  return [...byModule.entries()].map(([module, { key }]) => ({
    id: `perm:${module}`,
    text: getPermissionLabel(key, t),
    included: true,
  }));
}

/**
 * T-449 — shared "what's included" list for plan / add-on cards (platform
 * console + tenant My Plan). Fully derived from catalog data (limits,
 * includedModules, permissionKeys) — no hand-written bullets — so a catalog
 * change surfaces here without editing any card.
 */
export function IncludedFeatures(props: IncludedFeaturesProps) {
  const { t } = useTranslation('settings');
  const translate: Translate = (key, options) => t(key, options) as string;
  const rows = props.kind === 'plan' ? planRows(props.plan, translate) : addonRows(props.addon, translate);
  if (rows.length === 0) return null;

  const max = props.max;
  const visible = max !== undefined && rows.length > max ? rows.slice(0, max) : rows;
  const hidden = rows.length - visible.length;

  return (
    <div className={cn('space-y-1', props.className)}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('includesTitle')}</p>
      <ul className="space-y-0.5">
        {visible.map((row) => (
          <li
            key={row.id}
            className={cn('flex items-start gap-1.5 text-xs', row.included ? 'text-foreground' : 'text-muted-foreground')}
          >
            {row.included ? (
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-success-text" aria-hidden="true" />
            ) : (
              <Minus className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            <span>{row.text}</span>
          </li>
        ))}
        {hidden > 0 && <li className="ps-[18px] text-xs text-muted-foreground">{t('includesMore', { n: hidden })}</li>}
      </ul>
    </div>
  );
}
